import React from 'react';
import { ChevronDown, Phone, Share2 } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const slug = (name) => name.toLowerCase().replace(/\s+/g, '-');

const isRoute = (href) => href && href !== '#' && !href.startsWith('#');

const MobileNav = ({
  navLinks,
  openDropdown,
  setOpenDropdown,
  onClose,
  onOpenReferralForm,
  onOpenReferralModal
}) => {
  const { pathname } = useLocation();

  const isLinkActive = (href) => isRoute(href) && pathname === href;
  const isParentActive = (link) =>
    isLinkActive(link.href) ||
    (link.dropdownItems || []).some((item) => !item.skipParentHighlight && isLinkActive(item.href));

  return (
    <div className="2xl:hidden pb-4" data-testid="mobile-menu">
      {navLinks.map((link) => {
        const active = link.hasDropdown ? isParentActive(link) : isLinkActive(link.href);
        return (
          <div key={link.name}>
            {link.hasDropdown ? (
              <div>
                <div className="flex items-center justify-between w-full py-2">
                  {link.href && link.href !== '#' ? (
                    <Link
                      to={link.href}
                      className={active ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}
                      onClick={onClose}
                      data-testid={`mobile-nav-${link.dropdownKey}-parent`}
                      aria-current={active ? 'page' : undefined}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <span
                      className={active ? 'text-blue-600 font-semibold' : 'text-gray-700'}
                      data-testid={`mobile-nav-${link.dropdownKey}-parent`}
                      aria-current={active ? 'page' : undefined}
                    >
                      {link.name}
                    </span>
                  )}
                  <button
                    onClick={() => setOpenDropdown(openDropdown === link.dropdownKey ? null : link.dropdownKey)}
                    className={`p-1 hover:text-blue-600 ${active ? 'text-blue-600' : 'text-gray-700'}`}
                    aria-expanded={openDropdown === link.dropdownKey}
                    aria-haspopup="true"
                    aria-label={`Toggle ${link.name} menu`}
                    data-testid={`mobile-nav-${link.dropdownKey}-toggle`}
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        openDropdown === link.dropdownKey ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>
                {openDropdown === link.dropdownKey && (
                  <div className="pl-4 space-y-2">
                    {link.dropdownItems.map((item) => {
                      const itemActive = isLinkActive(item.href);
                      const itemClasses = itemActive
                        ? 'text-blue-600 font-semibold'
                        : 'text-gray-600 hover:text-blue-600';
                      return item.action === 'referralForm' ? (
                        <button
                          key={item.name}
                          onClick={() => {
                            onOpenReferralForm();
                            onClose();
                            setOpenDropdown(null);
                          }}
                          className={`block w-full text-left py-2 ${itemClasses}`}
                          data-testid="mobile-nav-submit-referral-btn"
                        >
                          {item.name}
                        </button>
                      ) : item.href.startsWith('#') ? (
                        <a
                          key={item.name}
                          href={item.href}
                          className={`block py-2 ${itemClasses}`}
                          onClick={() => { onClose(); setOpenDropdown(null); }}
                          data-testid={`mobile-nav-item-${slug(item.name)}`}
                        >
                          {item.name}
                        </a>
                      ) : (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`block py-2 ${itemClasses}`}
                          onClick={() => { onClose(); setOpenDropdown(null); }}
                          data-testid={`mobile-nav-item-${slug(item.name)}`}
                          aria-current={itemActive ? 'page' : undefined}
                        >
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : link.href.startsWith('/#') ? (
              <a
                href={link.href}
                className="block py-2 text-gray-700 hover:text-blue-600"
                onClick={onClose}
                data-testid={`mobile-nav-link-${slug(link.name)}`}
              >
                {link.name}
              </a>
            ) : (
              <Link
                to={link.href}
                className={`block py-2 ${active ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={onClose}
                data-testid={`mobile-nav-link-${slug(link.name)}`}
                aria-current={active ? 'page' : undefined}
              >
                {link.name}
              </Link>
            )}
          </div>
        );
      })}

      {/* Mobile CTA */}
      <div className="border-t border-gray-200 pt-4 mt-4 space-y-3">
        <a
          href="tel:+18777725150"
          className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600 whitespace-nowrap"
          data-testid="mobile-nav-phone-link"
        >
          <Phone className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">(877) 772-5150</span>
        </a>

        <Link
          to="/intake"
          onClick={onClose}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all font-bold shadow-md whitespace-nowrap"
          data-testid="mobile-get-started-btn"
        >
          <span>GET STARTED</span>
        </Link>

        <button
          onClick={() => {
            onOpenReferralModal();
            onClose();
          }}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 rounded-lg font-semibold whitespace-nowrap transition-colors hover:bg-teal-50"
          style={{ borderColor: '#66C2B6', color: '#499F94' }}
          aria-label="Extend the Circle of Care"
          data-testid="mobile-circle-of-care-btn"
        >
          <Share2 className="w-4 h-4 flex-shrink-0" />
          <span>Extend the Circle of Care</span>
        </button>
      </div>
    </div>
  );
};

export default MobileNav;
